import { useMemo, useState } from "react";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { z } from "zod";
import { Check, Send, Info } from "lucide-react";
import { api } from "@/lib/api";
import { calcFee, withTax, taxAmount, calcPayout } from "@/lib/fee";

interface Props {
  caseId: string;
  minAmount: number;
}

/**
 * 見積もり明細の項目（2026-07-03 中島社長要望）。
 * 出張費・工事費などを項目入力し、合計を見積もり金額とする。
 * 事務員・新人が入力しても計算ミスが出ないよう合計は自動計算。
 * placeholder に薄グレーで入力見本を出す。機器（本体）代は別途（お客様購入）で見積もりに含めない。
 */
interface LineItem {
  key: string;
  label: string;
  example: number; // 入力見本（placeholder表示）
  required?: boolean;
}
const LINE_ITEMS: LineItem[] = [
  { key: "travel", label: "出張費", example: 5000 },
  { key: "work", label: "標準工事費", example: 20000, required: true },
  { key: "parts", label: "配管・部材費", example: 6000 },
  { key: "removal", label: "撤去・リサイクル費", example: 4000 },
  { key: "other", label: "その他（追加工事など）", example: 0 },
];

const makeSchema = (min: number) =>
  z.object({
    amount: z.coerce.number().int().min(min, `最低受注額は ¥${min.toLocaleString("ja-JP")} です`),
    duration_days: z.coerce.number().int().min(1).max(60),
    date1: z.string().min(1, "第1候補日を選択してください"),
    date2: z.string().optional(),
    date3: z.string().optional(),
    notes: z.string().max(2000).optional(),
  });

type FormErrors = Partial<Record<"amount" | "duration_days" | "date1" | "form", string>>;

const DAY_OPTIONS = [1, 2, 3, 5];

export function BidForm({ caseId, minAmount }: Props) {
  const qc = useQueryClient();
  // 明細（項目→金額）。初期は空欄（placeholderに見本）。
  const [items, setItems] = useState<Record<string, number>>({});
  const [duration, setDuration] = useState(1);
  const [date1, setDate1] = useState("");
  const [date2, setDate2] = useState("");
  const [date3, setDate3] = useState("");
  const [notes, setNotes] = useState("");
  const [errors, setErrors] = useState<FormErrors>({});
  const [submitted, setSubmitted] = useState(false);

  // 合計＝各項目の合算。これが見積もり金額（税抜）になる。
  const amount = useMemo(
    () => LINE_ITEMS.reduce((sum, it) => sum + (items[it.key] || 0), 0),
    [items],
  );
  const fee = useMemo(() => calcFee(amount), [amount]);      // 税抜×20%
  const gross = useMemo(() => withTax(amount), [amount]);     // 税込（顧客支払）
  const tax = useMemo(() => taxAmount(amount), [amount]);     // 消費税10%
  const net = useMemo(() => calcPayout(amount), [amount]);    // 手取り＝税込−手数料

  const setItem = (key: string, v: string) =>
    setItems((prev) => ({ ...prev, [key]: parseInt(v) || 0 }));

  const mut = useMutation({
    mutationFn: () =>
      api.createBid({
        case_id: caseId,
        amount,
        proposed_date: date1 || null,
        comment: [
          // 明細を内訳として保存（顧客・管理が後から確認できる）
          "【見積もり内訳（税抜）】",
          ...LINE_ITEMS.filter((it) => (items[it.key] || 0) > 0).map(
            (it) => `・${it.label}: ¥${(items[it.key] || 0).toLocaleString("ja-JP")}`,
          ),
          `・小計(税抜): ¥${amount.toLocaleString("ja-JP")}`,
          `・消費税(10%): ¥${tax.toLocaleString("ja-JP")}`,
          `・合計(税込): ¥${gross.toLocaleString("ja-JP")}`,
          "※エアコン本体（機器）代はお客様購入のため別途（本見積もりに含みません）",
          notes ? `\n${notes}` : "",
          `工期目安: ${duration}日`,
          [date1, date2, date3].filter(Boolean).length > 1
            ? `候補日: ${[date1, date2, date3].filter(Boolean).join(" / ")}`
            : "",
        ].filter(Boolean).join("\n") || null,
      }),
    onSuccess: () => {
      setSubmitted(true);
      qc.invalidateQueries({ queryKey: ["case", caseId] });
      qc.invalidateQueries({ queryKey: ["my-bids"] });
    },
    onError: (e: Error) => setErrors({ form: e.message }),
  });

  const submit = (ev: React.FormEvent) => {
    ev.preventDefault();
    setErrors({});
    const parsed = makeSchema(minAmount).safeParse({ amount, duration_days: duration, date1, date2, date3, notes });
    if (!parsed.success) {
      const err: FormErrors = {};
      for (const issue of parsed.error.issues) {
        const key = issue.path[0] as keyof FormErrors;
        err[key] = issue.message;
      }
      setErrors(err);
      return;
    }
    mut.mutate();
  };

  if (submitted) {
    return (
      <div className="rounded-xl border border-brand-500 bg-brand-50 dark:bg-brand-800/30 p-4 text-sm mt-4">
        <p className="font-bold">入札を送信しました</p>
        <p className="text-ink-soft dark:text-white/70 mt-1">
          顧客の選定をお待ちください。結果は「受注管理」タブで確認できます。
        </p>
      </div>
    );
  }

  const belowMin = amount < minAmount;

  return (
    <form onSubmit={submit} className="space-y-3 mt-4">
      {/* 見積もり明細（項目ごとに入力→合計自動計算） */}
      <div className="bg-white dark:bg-white/5 rounded-xl p-4 border border-[var(--color-border)]">
        <div className="flex items-baseline justify-between">
          <label className="text-xs font-bold text-ink-soft dark:text-white/60">見積もり明細（税抜で入力）</label>
          <span className="text-[11px] text-ink-soft dark:text-white/50">薄い数字は入力見本</span>
        </div>
        <div className="mt-2 divide-y divide-[var(--color-border)]">
          {LINE_ITEMS.map((it) => (
            <div key={it.key} className="flex items-center justify-between py-2 gap-3">
              <span className="text-sm">
                {it.label}
                {it.required && <span className="ml-1 text-red-500 text-xs">*</span>}
              </span>
              <div className="flex items-baseline gap-1">
                <span className="text-sm text-ink-soft">¥</span>
                <input
                  type="number"
                  inputMode="numeric"
                  step={500}
                  value={items[it.key] ? items[it.key] : ""}
                  onChange={(e) => setItem(it.key, e.target.value)}
                  placeholder={it.example ? it.example.toLocaleString("ja-JP") : "0"}
                  className="w-28 text-right text-base font-bold outline-none border-b border-dashed border-[var(--color-border)] focus:border-brand-500 bg-transparent tabular-nums placeholder:text-ink-soft/30 placeholder:font-normal"
                />
              </div>
            </div>
          ))}
        </div>

        {/* 合計（税抜→消費税→税込） */}
        <div className="mt-3 pt-3 border-t-2 border-brand-500/40 space-y-1 tabular-nums">
          <div className="flex items-baseline justify-between text-sm">
            <span className="text-ink-soft dark:text-white/60">小計（税抜）</span>
            <span className="font-bold">¥{amount.toLocaleString("ja-JP")}</span>
          </div>
          <div className="flex items-baseline justify-between text-xs text-ink-soft dark:text-white/60">
            <span>消費税（10%）</span>
            <span>¥{tax.toLocaleString("ja-JP")}</span>
          </div>
          <div className="flex items-baseline justify-between pt-1">
            <span className="text-sm font-bold">お客様支払額（税込）</span>
            <span className="text-3xl font-black">¥{gross.toLocaleString("ja-JP")}</span>
          </div>
        </div>

        {belowMin ? (
          <p className="mt-2 text-xs text-red-600 font-bold">
            最低受注額 ¥{minAmount.toLocaleString("ja-JP")} を下回っています（合計を調整してください）
          </p>
        ) : (
          <p className="mt-2 text-xs text-brand-700 dark:text-brand-300 font-bold flex items-center gap-1">
            <Check size={12} /> 最低受注額 ¥{minAmount.toLocaleString("ja-JP")} を満たしています
          </p>
        )}
        {errors.amount && <p className="mt-1 text-xs text-red-600">{errors.amount}</p>}
      </div>

      {/* 機器代は別途（お客様購入）の明示 */}
      <div className="bg-amber-50 dark:bg-amber-900/20 border border-amber-200 dark:border-amber-800/40 rounded-xl p-3 flex items-start gap-2">
        <Info size={14} className="text-amber-600 dark:text-amber-300 mt-0.5 flex-shrink-0" />
        <p className="text-xs text-amber-800 dark:text-amber-200 leading-relaxed">
          <b>エアコン本体（機器）代は別途</b>です。本体はお客様がアプリ上で購入し、業者または自宅へ配送されます。
          上の見積もりには<b>工事費のみ</b>を入力してください。
        </p>
      </div>

      {/* リアルタイム手数料・手取り */}
      <div className="bg-brand-50 dark:bg-brand-800/20 border border-brand-200 dark:border-brand-700 rounded-xl p-4">
        <div className="text-xs font-bold text-brand-900 dark:text-brand-100 mb-2">手数料・手取り（リアルタイム計算）</div>
        <div className="space-y-2 tabular-nums">
          <div className="flex justify-between text-sm">
            <span className="text-brand-800 dark:text-brand-200">見積もり金額（税抜）</span>
            <span className="font-bold">¥{amount.toLocaleString("ja-JP")}</span>
          </div>
          <div className="flex justify-between text-sm">
            <span className="text-brand-800 dark:text-brand-200">お客様支払額（税込）</span>
            <span className="font-bold">¥{gross.toLocaleString("ja-JP")}</span>
          </div>
          <div className="flex justify-between text-sm">
            <span className="text-brand-800 dark:text-brand-200">プラットフォーム手数料（税抜×20%）</span>
            <span className="font-bold text-red-600">−¥{fee.toLocaleString("ja-JP")}</span>
          </div>
          <div className="pt-2 mt-2 border-t border-brand-300 dark:border-brand-600 flex justify-between items-baseline">
            <span className="text-sm font-bold text-brand-900 dark:text-brand-100">受取見込額（税込−手数料）</span>
            <span className="text-2xl font-black text-brand-700 dark:text-brand-300">¥{net.toLocaleString("ja-JP")}</span>
          </div>
        </div>
      </div>

      <div className="bg-white dark:bg-white/5 rounded-xl p-3 border border-[var(--color-border)] text-xs text-ink-soft dark:text-white/60">
        <div className="font-bold text-[var(--color-fg)] mb-1">プラットフォーム手数料</div>
        <div>見積もり金額の <b>一律 20%</b>（上限なし・分かりやすい料率）。残りが手取りになります。</div>
      </div>

      {/* 工期 */}
      <div className="bg-white dark:bg-white/5 rounded-xl p-4 border border-[var(--color-border)]">
        <label className="text-xs font-bold text-ink-soft dark:text-white/60">工期（日）</label>
        <div className="mt-2 flex gap-2">
          {DAY_OPTIONS.map((d) => (
            <button
              key={d}
              type="button"
              onClick={() => setDuration(d)}
              className={`px-4 py-2 rounded-lg text-sm font-bold ${duration === d ? "bg-brand-500 text-white" : "bg-gray-100 dark:bg-white/10 text-[var(--color-fg)]"}`}
            >
              {d}日
            </button>
          ))}
          <input
            type="number"
            inputMode="numeric"
            min={1}
            max={60}
            value={duration}
            onChange={(e) => setDuration(parseInt(e.target.value) || 1)}
            className="w-16 border border-[var(--color-border)] rounded-lg px-2 text-sm bg-transparent tabular-nums"
            aria-label="工期（日数を直接入力）"
          />
        </div>
      </div>

      {/* 候補日 */}
      <div className="bg-white dark:bg-white/5 rounded-xl p-4 border border-[var(--color-border)]">
        <label className="text-xs font-bold text-ink-soft dark:text-white/60">訪問希望日（3候補まで）</label>
        <div className="grid grid-cols-3 gap-2 mt-2">
          <input type="date" value={date1} onChange={(e) => setDate1(e.target.value)} className="border border-[var(--color-border)] rounded-md px-2 py-2 bg-transparent text-sm" />
          <input type="date" value={date2} onChange={(e) => setDate2(e.target.value)} className="border border-[var(--color-border)] rounded-md px-2 py-2 bg-transparent text-sm" />
          <input type="date" value={date3} onChange={(e) => setDate3(e.target.value)} className="border border-[var(--color-border)] rounded-md px-2 py-2 bg-transparent text-sm" />
        </div>
        {errors.date1 && <p className="mt-1 text-xs text-red-600">{errors.date1}</p>}
      </div>

      {/* 備考 */}
      <div className="bg-white dark:bg-white/5 rounded-xl p-4 border border-[var(--color-border)]">
        <label className="text-xs font-bold text-ink-soft dark:text-white/60">アピール・条件（保証・追加料金など）</label>
        <textarea
          value={notes}
          onChange={(e) => setNotes(e.target.value)}
          rows={3}
          className="w-full mt-2 border border-[var(--color-border)] rounded-md px-2 py-2 bg-transparent text-sm"
          placeholder="例: 同型番の在庫があり即日対応可能です。撤去・リサイクルまで一括対応します。"
        />
      </div>

      {errors.form && <p className="text-sm text-red-600">{errors.form}</p>}

      <button
        type="submit"
        disabled={mut.isPending || belowMin}
        className="w-full py-4 bg-brand-500 hover:bg-brand-600 text-white rounded-xl font-bold flex items-center justify-center gap-2 disabled:opacity-50 active:scale-95 transition"
      >
        <Send size={16} />
        {mut.isPending ? "送信中..." : "この内容で入札する"}
      </button>
    </form>
  );
}
